class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.announcementBar = this.querySelector("#wt-announcement__container");
    this.announcementInterval = null;
    
    // Carousel properties
    this.carouselInterval = null;
    this.carouselSpeed = 5000; // Default 5 seconds
    this.carouselItems = null;
    this.prevButton = null;
    this.nextButton = null;
    this.carouselWrapper = null;
    this.hoverTimeout = null;
    this.isHovering = false;

    this.changeAnnouncement = this.changeAnnouncement.bind(this);
    this.previousAnnouncement = this.previousAnnouncement.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.hideForOneDay = this.hideForOneDay.bind(this);
    this.restartInterval = this.restartInterval.bind(this);
    this.isMobileView = this.isMobileView.bind(this);
    
    // Carousel methods
    this.initializeCarousel = this.initializeCarousel.bind(this);
    this.updateCarousel = this.updateCarousel.bind(this);
    this.showNext = this.showNext.bind(this);
    this.showPrev = this.showPrev.bind(this);
    this.startCarousel = this.startCarousel.bind(this);
    this.stopCarousel = this.stopCarousel.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    // Initialize carousel if buttons are present
    if (this.announcementBar?.classList.contains("wt-announcement__container--with-buttons")) {
      setTimeout(() => {
        this.initializeCarousel();
      }, 100);
    }

    if (
      !this.announcementBar?.classList.contains(
        "wt-announcement__container--marquee",
      )
    ) {
      this.announcementInterval = setInterval(this.changeAnnouncement, 5000);

      if (this.announcementBar) {
        this.announcementBar.addEventListener(
          "touchstart",
          this.handleTouchStart,
          { passive: true },
        );
        this.announcementBar.addEventListener(
          "touchmove",
          this.handleTouchMove,
          {
            passive: true,
          },
        );
        this.announcementBar.addEventListener("touchend", this.handleTouchEnd, {
          passive: true,
        });
        this.announcementBar.addEventListener(
          "touchstart",
          () => clearInterval(this.announcementInterval),
          { passive: true },
        );
        this.announcementBar.addEventListener(
          "touchend",
          this.restartInterval,
          {
            passive: true,
          },
        );
      }
    }

    if (this.announcementBar)
      this.announcementBar.style.transition = "transform 0.5s ease-in-out";

    this.closeButton = this.querySelector(".wt-announcement__close");
    if (this.closeButton)
      this.closeButton.addEventListener("click", this.hideForOneDay);

    window.addEventListener("resize", this.handleResize);
  }

  handleResize() {
    if (window.innerWidth >= 900) {
      // Reset the translateX to its initial state
      if (this.announcementBar) {
        this.announcementBar.style.transform = "translateX(0)";
        this.currentIndex = 0;
      }
    }
  }

  isMobileView() {
    return window.innerWidth < 900;
  }

  restartInterval() {
    clearInterval(this.announcementInterval);
    this.announcementInterval = setInterval(this.changeAnnouncement, 5000);
  }

  hideForOneDay() {
    const oneDayLater = new Date().getTime() + 24 * 60 * 60 * 1000;
    localStorage.setItem("wt-announcement-hidden", oneDayLater);
    this.style.display = "none";
  }

  changeAnnouncement() {
    if (!this.isMobileView()) {
      return;
    }

    const totalSlides = this.announcementBar?.children.length;

    // Move to the next slide
    this.currentIndex = (this.currentIndex + 1) % totalSlides;
    const newPosition = -(this.currentIndex * 100);

    if (this.announcementBar)
      this.announcementBar.style.transform = `translateX(${newPosition}vw)`;

    if (this.currentIndex === 0 && this.announcementBar) {
      // When we're back at the start, reset position with transition.
      setTimeout(() => {
        this.announcementBar.style.transition = "none";
        this.announcementBar.style.transform = "translateX(0)";
        this.currentIndex = 0;
        // Restore transition after resetting position
        setTimeout(() => {
          this.announcementBar.style.transition = "transform 0.5s ease-in-out";
        }, 0);
      }, 490);
    }
  }

  previousAnnouncement() {
    const totalSlides = this.announcementBar?.children.length;

    this.currentIndex = (this.currentIndex - 1 + totalSlides) % totalSlides;
    const newPosition = -(this.currentIndex * 100);
    if (this.announcementBar)
      this.announcementBar.style.transform = `translateX(${newPosition}vw)`;
  }

  handleTouchStart(e) {
    if (this.isMobileView()) {
      this.touchStartX = e.touches[0].clientX;
    }
  }

  handleTouchMove(e) {
    if (this.isMobileView()) {
      this.touchEndX = e.touches[0].clientX;
    }
  }

  handleTouchEnd() {
    const swipeThreshold = 30;

    if (Math.abs(this.touchEndX - this.touchStartX) > swipeThreshold) {
      if (this.touchEndX < this.touchStartX) {
        this.changeAnnouncement();
      } else {
        this.previousAnnouncement();
      }
    }
  }

  // Carousel methods
  initializeCarousel() {
    if (!this.announcementBar) return;
    if (this.announcementBar.classList.contains("wt-announcement__container--marquee")) return;

    this.carouselItems = this.querySelectorAll(".wt-announcement__carousel-block");
    if (!this.carouselItems || this.carouselItems.length <= 1) return;

    this.prevButton = this.querySelector('[data-carousel-nav="prev"]');
    this.nextButton = this.querySelector('[data-carousel-nav="next"]');
    this.carouselWrapper = this.querySelector(".wt-announcement__carousel-wrapper");

    if (!this.prevButton || !this.nextButton) {
      console.warn('AnnouncementBar: Carousel navigation buttons not found');
      return;
    }

    // Get carousel speed from data attribute or use default
    const speedAttribute = this.announcementBar.dataset.carouselSpeed;
    this.carouselSpeed = speedAttribute ? parseInt(speedAttribute, 10) * 1000 : 5000;

    // Find the first active item
    this.currentIndex = 0;
    this.carouselItems.forEach((item, index) => {
      if (item.classList.contains("active")) {
        this.currentIndex = index;
      }
    });

    // Setup navigation buttons
    this.prevButton.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.stopCarousel();
      this.showPrev();
      this.startCarousel();
    });

    this.nextButton.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.stopCarousel();
      this.showNext();
      this.startCarousel();
    });

    // Improved hover detection for desktop
    if (window.innerWidth >= 900) {
      const contentWrapper = this.querySelector(".wt-announcement__content-wrapper");
      const carouselWrapper = this.querySelector(".wt-announcement__carousel-wrapper");
      
      // Function to check if we should pause (over carousel area)
      const shouldPause = (element) => {
        if (!element) return false;
        return element.closest('.wt-announcement__content-wrapper') ||
               element.closest('.wt-announcement__carousel-wrapper') ||
               element.closest('.wt-announcement__carousel-nav');
      };

      // Mouse enter on carousel areas
      const handleMouseEnter = (e) => {
        if (shouldPause(e.target)) {
          this.isHovering = true;
          if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
            this.hoverTimeout = null;
          }
          this.stopCarousel();
        }
      };

      // Mouse leave from carousel areas
      const handleMouseLeave = (e) => {
        // Only restart if we're truly leaving the carousel area
        if (!shouldPause(e.relatedTarget)) {
          this.isHovering = false;
          if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
          this.hoverTimeout = setTimeout(() => {
            if (!this.isHovering) {
              this.startCarousel();
            }
            this.hoverTimeout = null;
          }, 150);
        }
      };

      // Add listeners to relevant areas
      if (contentWrapper) {
        contentWrapper.addEventListener("mouseenter", handleMouseEnter);
        contentWrapper.addEventListener("mouseleave", handleMouseLeave);
      }
      
      if (carouselWrapper) {
        carouselWrapper.addEventListener("mouseenter", handleMouseEnter);
        carouselWrapper.addEventListener("mouseleave", handleMouseLeave);
      }

      // Store for cleanup
      this._mouseEnterHandler = handleMouseEnter;
      this._mouseLeaveHandler = handleMouseLeave;
    }

    // Initialize button states
    this.updateCarousel();

    // Start auto-rotation
    this.startCarousel();
  }

  updateCarousel() {
    if (!this.carouselItems) return;

    this.carouselItems.forEach((item, index) => {
      item.classList.toggle("active", index === this.currentIndex);
    });

    // Update button states - enable/disable based on position
    if (this.prevButton) {
      this.prevButton.disabled = this.currentIndex === 0;
    }
    if (this.nextButton) {
      this.nextButton.disabled = this.currentIndex === this.carouselItems.length - 1;
    }
  }

  showNext() {
    if (!this.carouselItems) return;
    
    if (this.currentIndex < this.carouselItems.length - 1) {
      this.currentIndex++;
    } else {
      this.currentIndex = 0; // Loop back to start
    }
    this.updateCarousel();
  }

  showPrev() {
    if (!this.carouselItems) return;
    
    if (this.currentIndex > 0) {
      this.currentIndex--;
    } else {
      this.currentIndex = this.carouselItems.length - 1; // Loop to end
    }
    this.updateCarousel();
  }

  startCarousel() {
    this.stopCarousel();
    if (this.carouselSpeed > 0 && !this.isHovering) {
      this.carouselInterval = setInterval(() => {
        if (!this.isHovering) {
          this.showNext();
        }
      }, this.carouselSpeed);
    }
  }

  stopCarousel() {
    if (this.carouselInterval) {
      clearInterval(this.carouselInterval);
      this.carouselInterval = null;
    }
  }

  disconnectedCallback() {
    clearInterval(this.announcementInterval);
    this.stopCarousel();
    
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
    
    if (this.announcementBar) {
      this.announcementBar.removeEventListener("touchstart", this.handleTouchStart);
      this.announcementBar.removeEventListener("touchmove", this.handleTouchMove);
      this.announcementBar.removeEventListener("touchend", this.handleTouchEnd);
    }
    
    // Cleanup hover listeners
    if (this._mouseEnterHandler && this._mouseLeaveHandler) {
      const contentWrapper = this.querySelector(".wt-announcement__content-wrapper");
      const carouselWrapper = this.querySelector(".wt-announcement__carousel-wrapper");
      
      if (contentWrapper) {
        contentWrapper.removeEventListener("mouseenter", this._mouseEnterHandler);
        contentWrapper.removeEventListener("mouseleave", this._mouseLeaveHandler);
      }
      
      if (carouselWrapper) {
        carouselWrapper.removeEventListener("mouseenter", this._mouseEnterHandler);
        carouselWrapper.removeEventListener("mouseleave", this._mouseLeaveHandler);
      }
    }
    
    window.removeEventListener("resize", this.handleResize);
  }
}

customElements.define("announcement-bar", AnnouncementBar);